import { useState, type FormEvent } from "react";
import { useAuth, useWorkspace } from "../lib/session";
import { createWorkspace } from "../lib/onboardApi";
import { readText, readWebsite } from "../lib/setupApi";
import { env, functionUrl } from "../lib/env";
import { httpFetch } from "../lib/http";
import { Logo } from "../components/Logo";
import { IconTile } from "../components/kit";
import { Building2 } from "lucide-react";
// Onboarding, screen 1 of 2: the business name, and one optional line about the business or its website (fix plan
// PR 2). The setup function reads that line once the workspace exists and saves a draft of what the business sells and
// to whom, which the first campaign's offer starts from. A reading that fails never holds the owner up.
// Then Ogma opens (screens/setup/OpenOgma) and Ask Ogma walks through the rest (lib/firstSteps.ts).
import "../components/setup/setup.css";

export function CreateWorkspace() {
  const { client, session } = useAuth();
  const { refresh } = useWorkspace();
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2) {
      setError("Enter your business name.");
      return;
    }
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      await createWorkspace({ url: functionUrl("onboard"), token: session.access_token, anonKey: env.supabaseAnonKey, name: n, fetchImpl: httpFetch });
      const line = about.trim();
      if (line) {
        const opts = { token: session.access_token, anonKey: env.supabaseAnonKey, fetchImpl: httpFetch };
        await (looksLikeWebsite(line) ? readWebsite(line, opts) : readText(line, opts)).catch(() => undefined);
      }
      await refresh();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <Logo width={180} className="auth-logo" />
      <div className="auth-card">
        <IconTile icon={Building2} tone="seal" size="lg" />
        <h1>Create your workspace</h1>
        <p className="lede">One workspace per business. Only people you add can see it.</p>
        <form onSubmit={submit} noValidate>
          <label className="field" htmlFor="biz">
            <span className="field-label">Business name</span>
            <input id="biz" autoComplete="organization" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} aria-invalid={Boolean(error)} />
          </label>
          <label className="field" htmlFor="about">
            <span className="field-label">What do you sell, and to whom? (optional)</span>
            <span className="field-hint">A line or two, or your website address. Ogma uses it to write your first offer.</span>
            <textarea id="about" rows={2} maxLength={600} value={about} onChange={(e) => setAbout(e.target.value)} />
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? (about.trim() ? "Creating and reading about your business…" : "Creating…") : "Create workspace"}
          </button>
        </form>
        <p className="muted small">
          Signed in as {session?.user.email}.{" "}
          <button type="button" className="link-btn" onClick={() => void client.auth.signOut()}>
            Use a different account
          </button>
        </p>
        <p className="muted small">Joining an existing business instead? Ask its owner to add you, then sign in again.</p>
      </div>
    </div>
  );
}

/** A website address rather than a sentence: one word with a dot in it, like acme.co.uk or https://acme.co.uk. */
export function looksLikeWebsite(line: string): boolean {
  return /^(https?:\/\/)?[^\s/]+\.[^\s]+$/i.test(line.trim());
}
